import { useMemo } from 'react';

import type { AccessLevelProp } from './useAccessLevelContext';
import { AccessLevel, useAccessLevelContext } from './useAccessLevelContext';

export type InternalProp = boolean | string;

/**
 * Picks which feature badge to render, for components that still accept the legacy `internal`
 * boolean alongside `accessLevel`. Precedence is `accessLevel` prop > local `internal` prop >
 * ancestor context, so an explicit prop always wins over an inherited value.
 *
 * A non-null result is display only and grants nothing; `null` is the only value that denies
 * access, and acting on it is the caller's job.
 */
export function useAccessLevelOrInternal({
  accessLevel,
  internal,
}: {
  accessLevel?: AccessLevelProp | null;
  internal?: InternalProp;
}) {
  const accessLevelInternalBackcompat = useMemo(() => {
    // Not a truthiness check: an explicit `null` denies access and must reach the caller rather
    // than falling through to the ancestor context.
    if (accessLevel !== undefined) return accessLevel;
    if (internal) return AccessLevel.Internal;
    return undefined;
  }, [accessLevel, internal]);
  return useAccessLevelContext(accessLevelInternalBackcompat);
}
